/* Por usuário: small multiples + a tabela completa classificador × mês.
 *
 * Filtro, busca e ordenação vivem na query string, então qualquer vista é
 * compartilhável — e o bug do painel antigo, em que trocar de usuário derrubava
 * o `&tab=` do deep link, deixa de existir por construção. */

import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, qs } from "../lib/api";
import type { SeriesPayload, Tile } from "../lib/api";
import { FLOWS, byName, cell, compact, fmtMes, shortName } from "../lib/format";
import { Sparkline } from "../charts/Sparkline";
import { HeatMatrix } from "../charts/HeatMatrix";
import { EntryPanel, entryTitle } from "../components/EntryPanel";
import { Card, ChartFrame, Empty, ErrorState, PageHead, Skeleton, SkeletonGrid, Tiles, useTooltip }
  from "../components/ui";
import { useActiveUser, useUsers } from "../components/Shell";

export function User() {
  const userId = useActiveUser();
  const [params, setParams] = useSearchParams();
  const { data: users } = useUsers();
  const { setTip, tipNode } = useTooltip();

  const flow = params.get("fluxo") || "";
  const query = params.get("q") || "";
  const sort = params.get("ordem") || "total";
  const hideFuture = params.get("futuros") === "0";
  const hideEmpty = params.get("vazios") === "0";
  const heat = params.get("heat") !== "0";

  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };

  const series = useQuery({
    queryKey: ["series", userId],
    queryFn: () => api<SeriesPayload>(`/api/users/${userId}/series`),
    enabled: !!userId,
  });
  const summary = useQuery({
    queryKey: ["summary", userId],
    queryFn: () => api<{ tiles: Tile[] }>(`/api/users/${userId}/summary`),
    enabled: !!userId,
  });

  const visible = useMemo(() => {
    if (!series.data) return [];
    const needle = query.trim().toLowerCase();
    let rows = series.data.series.filter((s) =>
      (!flow || s.flow === flow) &&
      (!needle || s.category.toLowerCase().includes(needle)));
    if (hideEmpty) rows = rows.filter((s) => s.active_months > 0);
    const order: Record<string, (a: typeof rows[0], b: typeof rows[0]) => number> = {
      total: (a, b) => b.realized_total - a.realized_total,
      nome: (a, b) => byName(a.category, b.category),
      ultimo: (a, b) => (b.last_realized_index ?? -1) - (a.last_realized_index ?? -1),
      grupo: (a, b) => FLOWS.indexOf(a.flow) - FLOWS.indexOf(b.flow)
                       || b.realized_total - a.realized_total,
    };
    return [...rows].sort(order[sort] ?? order.total);
  }, [series.data, flow, query, sort, hideEmpty]);

  const cut = series.data?.cut_index ?? 0;
  const labels = series.data?.labels ?? [];
  const shownLabels = hideFuture ? labels.slice(0, cut) : labels;
  const user = users?.find((u) => u.user_id === userId);

  const tableRows = useMemo(() => visible.map((s) => ({
    key: s.category, label: shortName(s.category), flow: s.flow,
    values: hideFuture ? s.values.slice(0, cut) : s.values,
    total: hideFuture ? s.realized_total : s.total,
  })), [visible, hideFuture, cut]);

  // Somatoria mensal, uma linha por fluxo. Calculada sobre as linhas VISIVEIS: um
  // cabecalho que soma o que a tela nao mostra e pior que cabecalho nenhum.
  const flowTotals = useMemo(() => FLOWS
    .filter((flow) => tableRows.some((r) => r.flow === flow))
    .map((flow) => {
      const rows = tableRows.filter((r) => r.flow === flow);
      return {
        key: flow, label: flow.toLowerCase(), flow,
        values: shownLabels.map((_, i) =>
          rows.reduce((total, r) => total + (r.values[i] ?? 0), 0)),
        total: rows.reduce((total, r) => total + (r.total ?? 0), 0),
      };
    }), [tableRows, shownLabels]);

  // Qual categoria (e, se houver, qual mes) esta aberta embaixo. Na query string,
  // entao a vista continua compartilhavel -- igual ao resto desta rota.
  const detail = params.get("detalhe") || "";
  const detailMonth = params.get("mes") || "";
  const detailRow = tableRows.find((r) => r.key === detail);
  const expected = detailRow
    ? (detailMonth
        ? detailRow.values[shownLabels.indexOf(detailMonth)] ?? 0
        : detailRow.total ?? 0)
    : null;

  // Com "só realizados" ligado, o total da linha exclui os meses agendados — e o
  // detalhe precisa excluir os mesmos, ou a linha de conferência acusa uma
  // divergência que é só de recorte. Com um mês escolhido a questão não existe:
  // ali o filtro do mês já é exato.
  const detailParams = {
    realized: hideFuture && !detailMonth ? 1 : undefined,
  };

  const openDetail = (category: string, month?: string) => {
    const next = new URLSearchParams(params);
    next.set("detalhe", category);
    if (month) next.set("mes", month); else next.delete("mes");
    setParams(next, { replace: true });
    requestAnimationFrame(() =>
      document.getElementById("lancamentos")?.scrollIntoView({ block: "nearest" }));
  };
  const closeDetail = () => {
    const next = new URLSearchParams(params);
    next.delete("detalhe");
    next.delete("mes");
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageHead eyebrow={`classificador · ${userId?.slice(0, 8) ?? ""}`}
                title="Por usuário">
        {user
          ? `${user.months} meses de ${fmtMes(user.first_month, true)} a ${fmtMes(user.last_month, true)}, ${user.categories} categorias, R$ ${compact(user.total)} de massa.`
          : "Evolução de cada classificador no tempo."}
      </PageHead>

      <div className="controls">
        <div className="chips" role="group" aria-label="Filtrar por tipo de fluxo">
          <button className="chip" aria-pressed={!flow} onClick={() => set("fluxo", "")}>todos</button>
          {FLOWS.map((f) => (
            <button key={f} className="chip" data-flow={f} aria-pressed={flow === f}
                    onClick={() => set("fluxo", flow === f ? "" : f)}>
              {f.toLowerCase()}
            </button>
          ))}
        </div>
        <input type="search" placeholder="Filtrar classificador…" value={query}
               aria-label="Filtrar classificador"
               onChange={(e) => set("q", e.target.value)} />
        <label>ordenar
          <select value={sort} onChange={(e) => set("ordem", e.target.value)}>
            <option value="total">maior total</option>
            <option value="grupo">tipo de fluxo</option>
            <option value="nome">nome</option>
            <option value="ultimo">mais recente</option>
          </select>
        </label>
        <label className="switch">
          <input type="checkbox" checked={hideFuture}
                 onChange={(e) => set("futuros", e.target.checked ? "0" : "")} />
          só realizados
        </label>
        <label className="switch">
          <input type="checkbox" checked={hideEmpty}
                 onChange={(e) => set("vazios", e.target.checked ? "0" : "")} />
          ocultar categorias vazias
        </label>
      </div>

      {summary.data && <Tiles items={summary.data.tiles} />}

      <div style={{ marginTop: 18 }}>
        <ChartFrame
          title="Evolução no tempo"
          sub={cut < labels.length
            ? `${labels.length - cut} meses ainda não realizados aparecem sombreados`
            : "todos os meses realizados"}
          guide="rotina"
        >
          {series.isPending && <SkeletonGrid />}
          {series.error && <ErrorState error={series.error} onRetry={() => series.refetch()} />}
          {series.data && !visible.length && (
            <Empty onClear={() => setParams(new URLSearchParams())}>
              Nenhum classificador corresponde ao filtro.
            </Empty>
          )}
          {!!visible.length && (
            <div className="sparkgrid">
              {visible.map((s) => (
                <div className="spark" key={s.category}>
                  <h3>{shortName(s.category)}</h3>
                  <div className="meta">
                    <span>R$ {compact(s.realized_total)}</span>
                    <span>{s.active_months} meses ativos</span>
                  </div>
                  <Sparkline
                    labels={labels}
                    values={hideFuture ? s.values.slice(0, cut) : s.values}
                    cutIndex={hideFuture ? cut : cut}
                    flow={s.flow}
                    title={s.category}
                    onHover={(info) => setTip(info && {
                      text: `${fmtMes(info.label, true)} · R$ ${cell(info.value)}`,
                      x: info.x, y: info.y,
                    })}
                  />
                </div>
              ))}
            </div>
          )}
        </ChartFrame>
      </div>

      <div style={{ marginTop: 18 }}>
        <Card
          title="Tabela completa"
          sub={`${visible.length} classificadores × ${shownLabels.length} meses · somatória mensal por fluxo no topo, subtotal ao fim de cada grupo · clique numa categoria ou num mês para ver os lançamentos`}
          right={
            <>
              <label className="switch">
                <input type="checkbox" checked={heat}
                       onChange={(e) => set("heat", e.target.checked ? "" : "0")} />
                escala de calor
              </label>
              <a className="btn" href={`/api/users/${userId}/series.csv`}>
                baixar CSV
              </a>
            </>
          }
        >
          {series.isPending && <Skeleton rows={10} />}
          {!!visible.length && (
            <HeatMatrix
              firstColumnLabel="classificador"
              columns={shownLabels.map((m) => ({ key: m, label: fmtMes(m) }))}
              rows={tableRows}
              normalize="row"
              heat={heat}
              groupBy="flow"
              summaryRows={flowTotals}
              onPickRow={(category) => openDetail(category)}
              onPickCell={(category, month) => openDetail(category, month)}
              activeRow={detail || null}
              activeColumn={detailMonth || null}
            />
          )}
        </Card>
      </div>

      {detail && (
        <div style={{ marginTop: 18 }}>
          <EntryPanel
            base={`/api/users/${userId}/entries`}
            params={{ category: detail, month: detailMonth || undefined,
                      realized: detailParams.realized }}
            title={entryTitle(detail, detailMonth)}
            sub={detailMonth ? undefined
                             : hideFuture ? "todos os meses realizados da linha"
                                          : "todos os meses da linha"}
            expected={expected}
            csvHref={`/api/users/${userId}/entries.csv${qs({
              category: detail, month: detailMonth,
              realized: detailParams.realized })}`}
            onClose={closeDetail}
          />
        </div>
      )}

      {tipNode}
    </>
  );
}
